import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { Transition, Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption } from '@headlessui/react'
import { Fragment, useRef, useState } from 'react';
import { IDropdown } from '@/constants/default';
import _ from 'lodash';

interface DropDownProps {
    list: IDropdown[],
    selected: any,
    setSelected: any
    isMultiple?: boolean
    placeholder?: string
    disabled?: boolean
}

export default function DropDownList({selected, setSelected, list, isMultiple = false, placeholder = 'Select item', disabled = false}: DropDownProps) {
    const [query, setQuery] = useState('')
    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const inputRef = useRef<HTMLInputElement | null>(null)

    const filteredList = query === '' ? list : list.filter((item) => item.label.toLowerCase().replace(/\s+/g, '').includes(query.toLowerCase().replace(/\s+/g, '')))

    const getNameFromValue = (value: string | number | string[] | number[]) => {
        if(isMultiple && _.isArray(value)) {
            const options = list.filter((option) => _.indexOf(value, option.value) >= 0) || [];
            return options.map((i: IDropdown) => i.label).join(", ")
        }
        const option = list.find((option) => option.value === value);
        return option ?  option.label : "";
    };

    return (
        <Combobox multiple={isMultiple} value={selected != undefined ? selected : ''} onChange={setSelected} disabled={disabled}>
            {({ open }) => (
                <div className="relative">
                    {/* ComboboxButton wraps entire input area for click-anywhere behavior */}
                    <ComboboxButton as="div" className="relative w-full cursor-pointer" disabled={disabled}>
                        <input
                            ref={inputRef}
                            type="text"
                            className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 pr-8 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary dark:text-white cursor-pointer"
                            value={getNameFromValue(selected)}
                            placeholder={placeholder}
                            disabled={disabled}
                            readOnly={true}
                        />
                        <span className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon
                                className="h-5 w-5 text-gray-400"
                                aria-hidden="true"
                            />
                        </span>
                    </ComboboxButton>
                    <Transition
                        show={open}
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setQuery('')}
                    >
                        <ComboboxOptions className="absolute z-10 max-h-56 w-full overflow-auto rounded-md py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm bg-white dark:border-form-strokedark dark:bg-form-input">
                        {filteredList.length === 0 && query !== '' ? (
                            <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                Nothing found.
                            </div>
                        ) : (
                            filteredList.map((item, index) => (
                                <ComboboxOption
                                    key={item.value}
                                    className="relative group cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-indigo-600 data-[focus]:text-white"
                                    value={item.value}
                                    disabled={selected === item.value}
                                >
                                    <span
                                        className="font-normal ml-3 block truncate dark:text-white group-data-[selected]:font-semibold"
                                    >
                                        {item.label}
                                    </span>
                                    <span
                                        className={"invisible absolute inset-y-0 right-0 flex items-center pr-4 text-white group-data-[selected]:text-primary group-data-[focus]:text-white group-data-[selected]:visible"}
                                    >
                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                </ComboboxOption>
                            ))
                        )}
                        </ComboboxOptions>
                    </Transition>
                </div>
            )}
        </Combobox>
    )
}
